import { useState, useEffect, useMemo } from "react";
import "./DashboardPresentation.css";
import { Link, useLocation } from "wouter";
import {
  LayoutDashboard,
  Calendar,
  Car,
  ClipboardList,
  Plane,
  DollarSign,
  MessageSquare,
  Heart,
  Star,
  Bell,
  Settings,
  LogOut,
  UserCircle,
  ArrowLeftRight,
  ShieldAlert,
  ArrowRight,
  Gift,
  HelpCircle,
  Users,
  User,
  Lock,
  Wallet,
  Globe,
  ChevronDown,
  Menu,
  Crown,
  BarChart3,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Logo } from "@/components/platform/Logo";
import { useAppTranslation } from "@/i18n/useAppTranslation";
import { useUser } from "@/hooks/useUser";
import { Avatar, AvatarImage, AvatarFallback } from "@workspace/ui/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuGroup,
} from "@workspace/ui/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@workspace/ui/components/ui/sheet";
import { useFeatureFlag } from "@/hooks/useFeatureFlags";
import { useAppMode } from "@/contexts/AppModeContext";
import { usePayoutTier } from "@/hooks/usePayoutTier";
import { bookingsApi, type TripListItem } from "@/lib/bookings-api";
import { subscriptionsApi } from "@/lib/subscriptions-api";
import { useDashboardUnreadMessages } from "@/hooks/useDashboardUnreadMessages";
import { DashboardMessageBadge } from "./DashboardMessageBadge";

interface NavItem {
  icon: LucideIcon;
  /** i18n key resolved with t() at render time (Task #7585). */
  labelKey: string;
  /**
   * Inline English fallback rendered via t(key, { defaultValue }) so a new
   * nav entry does not require the 8-locale lockstep gate (Task #8067).
   */
  fallbackLabel?: string;
  href: string;
}

// Task #1083: strict mode split. The two flat lists below replace the
// previous mixed sidebar — the active list is selected from `appMode`
// (not the URL) so the chrome and the page can never disagree about
// which "hat" the user is wearing.
//
// Shared label cross-references (@workspace/copy):
//   "Vehicles"      → HOST_NAV_VEHICLES      (nav.vehicles)
//   "Bookings"      → HOST_NAV_BOOKINGS      (nav.bookings)
//   "Earnings"      → HOST_NAV_EARNINGS      (nav.earnings)
//   "Reviews"       → HOST_NAV_REVIEWS       (nav.reviews)
//   "Notifications" → HOST_NAV_NOTIFICATIONS (nav.notifications)
// The mobile Today screen (today.tsx) consumes these constants directly;
// the web resolves them via t(entry.labelKey) from en.json.
const GUEST_NAV_ENTRIES: NavItem[] = [
  // Overview previously linked to /trips, identical to "My Trips" — the
  // duplicate entry was confusing on the sidebar (user feedback). Guests
  // only need a single Trips destination.
  { icon: Plane, labelKey: "nav.my_trips", href: "/trips" },
  // Only affects pages already using dashboard chrome, not header-based renter pages.
  { icon: MessageSquare, labelKey: "nav.messages", href: "/messages" },
  { icon: Heart, labelKey: "nav.favourites", href: "/favourites" },
  { icon: Bell, labelKey: "nav.notifications", href: "/notifications" },
  { icon: UserCircle, labelKey: "nav.profile", href: "/profile" },
  { icon: Settings, labelKey: "nav.settings", href: "/settings" },
];

const HOST_NAV_ENTRIES_BASE: NavItem[] = [
  { icon: LayoutDashboard, labelKey: "nav.overview", href: "/dashboard" },
  { icon: Calendar, labelKey: "nav.calendar", href: "/dashboard/calendar" },
  { icon: ClipboardList, labelKey: "nav.bookings", href: "/dashboard/bookings" },
  { icon: Car, labelKey: "nav.my_garage", href: "/dashboard/listings" },
  { icon: DollarSign, labelKey: "nav.earnings", href: "/dashboard/earnings" },
  { icon: MessageSquare, labelKey: "nav.messages", href: "/messages" },
  { icon: Star, labelKey: "nav.reviews", href: "/dashboard/reviews" },
  { icon: Bell, labelKey: "nav.notifications", href: "/dashboard/notifications" },
  { icon: UserCircle, labelKey: "nav.host_profile", href: "/dashboard/profile" },
];

const DRIVERS_NAV_ITEM: NavItem = {
  icon: Users,
  labelKey: "nav.drivers",
  href: "/dashboard/drivers",
};

// Tasks #20024/#20025 — DSv2 claims tracker entry. Only spliced in when
// BOTH cutover flags are on (mirrors the server's claimsFeatureEnabled), so
// no host sees a Claims tab before the claims feature is enabled.
const CLAIMS_NAV_ITEM: NavItem = {
  icon: ShieldCheck,
  labelKey: "nav.claims",
  fallbackLabel: "Claims",
  href: "/dashboard/claims",
};

const ANALYTICS_NAV_ITEM: NavItem = {
  icon: BarChart3,
  labelKey: "nav.analytics",
  fallbackLabel: "Analytics",
  href: "/dashboard/analytics",
};
const REFERRAL_NAV_ITEM: NavItem = {
  icon: Gift,
  labelKey: "nav.refer_earn",
  href: "/dashboard/referrals",
};

const SUBSCRIPTION_NAV_ITEM: NavItem = {
  icon: Crown,
  // Pro Owner is the product name, so keep it explicit in every locale
  // instead of presenting this destination as a generic billing screen.
  labelKey: "nav.pro_owner",
  fallbackLabel: "Pro Owner",
  href: "/dashboard/subscription",
};

// Only show a licence prompt when the authenticated driver has a real
// booking-scoped start/resume action; a payment or driving hold alone is not
// permission to launch another paid Didit session.
function VerificationHoldBanner() {
  const { t } = useAppTranslation();
  const { session } = useUser();
  const [held, setHeld] = useState<TripListItem[]>([]);

  useEffect(() => {
    let cancelled = false;
    if (!session?.access_token) return;
    bookingsApi
      .getMyTrips(session.access_token, "upcoming")
      .then((trips) => {
        if (cancelled) return;
        setHeld(trips.filter((trip) =>
          trip.verificationSummary?.action === "start" ||
          trip.verificationSummary?.action === "resume",
        ));
      })
      .catch(() => {
        // silent — banner is non-critical
      });
    return () => {
      cancelled = true;
    };
  }, [session?.access_token]);

  if (held.length === 0) return null;

  return (
    <div className="border-b border-amber-200 bg-amber-50 px-6 py-2.5 text-sm sm:px-8">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-x-6 gap-y-2">
        <div className="flex min-w-0 items-center gap-2 text-amber-900">
          <ShieldAlert className="h-4 w-4 shrink-0" />
          <span className="font-semibold">
            {t("dashboard.layout.verification_hold_title", { count: held.length })}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <Link
            href={held.length === 1 ? `/trips/${encodeURIComponent(held[0].public_id)}/verification` : "/trips"}
            className="inline-flex items-center gap-1 rounded-md bg-amber-900 px-3 py-1 text-xs font-semibold text-white hover:bg-amber-800"
          >
            {t(held.length === 1 ? "dashboard.layout.verify_now" : "dashboard.layout.view_booking")}
            <ArrowRight className="h-3 w-3" />
          </Link>
          <Link
            href={held.length === 1 ? `/booking/${held[0].public_id}` : "/trips"}
            className="text-xs font-semibold text-amber-900 underline-offset-4 hover:underline"
          >
            {t("dashboard.layout.view_booking", "View booking")}
          </Link>
        </div>
      </div>
    </div>
  );
}

export function DashboardLayout({
  children,
  presentation,
}: {
  children: React.ReactNode;
  /** Only explicitly opted-in host web journeys receive the warm content canvas. */
  presentation?: "refined";
}) {
  const { t } = useAppTranslation();
  const [location, navigate] = useLocation();
  const { signOut, profile, session } = useUser();
  const unreadMessages = useDashboardUnreadMessages();
  const { mode, canHost, toggle } = useAppMode();
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const referralsEnabled = useFeatureFlag("feature_referrals");

  // Task #1083: pick a nav list strictly from the persisted mode. URL is no
  // longer consulted — the ModeRouteSync guard in App.tsx already keeps mode
  // and URL in lock-step, so reading `mode` here is the single source of truth.
  const proOwnerEnabled = useFeatureFlag("feature_pro_owner");
  const [driversEntitled, setDriversEntitled] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const token = session?.access_token;
    if (mode !== "host" || !proOwnerEnabled || !token) {
      setDriversEntitled(false);
      return () => {
        cancelled = true;
      };
    }
    subscriptionsApi
      .getCurrentSubscription(token)
      .then(({ subscription }) => {
        if (!cancelled) {
          setDriversEntitled(
            !!subscription && ["active", "trialing", "past_due"].includes(subscription.status),
          );
        }
      })
      .catch(() => {
        if (!cancelled) setDriversEntitled(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, proOwnerEnabled, session?.access_token]);
  // Tasks #20024/#20025 — Claims tab requires the full DSv2 cutover pair.
  const brandCutover = useFeatureFlag("brand_cutover_driveshare");
  const claimsV2 = useFeatureFlag("feature_insurance_claims_v2");
  const claimsEnabled = brandCutover && claimsV2;
  // Tier 4 hosts have no payout rails available, so the Earnings tab is
  // hidden from their sidebar entirely. We wait until the tier is known
  // before stripping the entry to avoid a flash of "Earnings" then disappear
  // — while loading we keep the entry visible (matches all other tiers).
  const { tier: payoutTier, loading: payoutTierLoading } = usePayoutTier();
  const hideEarnings = !payoutTierLoading && payoutTier === 4;
  const navEntries = useMemo<NavItem[]>(() => {
    if (mode === "host") {
      let base = [...HOST_NAV_ENTRIES_BASE];
      if (hideEarnings) {
        base = base.filter((it) => it.href !== "/dashboard/earnings");
      }
      if (referralsEnabled) {
        const earningsIdx = base.findIndex((it) => it.href === "/dashboard/earnings");
        if (earningsIdx >= 0) {
          base.splice(earningsIdx + 1, 0, REFERRAL_NAV_ITEM);
        } else {
          base.push(REFERRAL_NAV_ITEM);
        }
      }
      if (claimsEnabled) {
        // Claims sits right after Bookings — a claim is always about a trip.
        const bookingsIdx = base.findIndex((it) => it.href === "/dashboard/bookings");
        if (bookingsIdx >= 0) {
          base.splice(bookingsIdx + 1, 0, CLAIMS_NAV_ITEM);
        } else {
          base.push(CLAIMS_NAV_ITEM);
        }
      }
      if (proOwnerEnabled) {
        if (driversEntitled) {
          const bookingsIdx = base.findIndex((it) => it.href === "/dashboard/bookings");
          if (bookingsIdx >= 0) {
            base.splice(bookingsIdx + 1, 0, DRIVERS_NAV_ITEM);
          } else {
            base.push(DRIVERS_NAV_ITEM);
          }
        }
        // Analytics sits right after Earnings (both are money/performance
        // surfaces); falls back to the end if Earnings is hidden (tier 4).
        const earningsIdx = base.findIndex((it) => it.href === "/dashboard/earnings");
        if (earningsIdx >= 0) {
          base.splice(earningsIdx + 1, 0, ANALYTICS_NAV_ITEM);
        } else {
          base.push(ANALYTICS_NAV_ITEM);
        }
        // Subscription nav item sits just before the Host Profile entry so
        // Pro Owner members can reach /dashboard/subscription from the sidebar.
        const profileIdx = base.findIndex((it) => it.href === "/dashboard/profile");
        if (profileIdx >= 0) {
          base.splice(profileIdx, 0, SUBSCRIPTION_NAV_ITEM);
        } else {
          base.push(SUBSCRIPTION_NAV_ITEM);
        }
      }
      return base;
    }
    return GUEST_NAV_ENTRIES;
  }, [mode, referralsEnabled, proOwnerEnabled, driversEntitled, hideEarnings, claimsEnabled]);

  const isActive = (href: string) => {
    // Exact match for dashboard root + guest profile, prefix match otherwise.
    if (href === "/dashboard") return location === "/dashboard";
    if (href === "/profile") return location === "/profile";
    if (href === "/dashboard/profile") return location === "/dashboard/profile";
    // /trips is shared by Overview and My Trips entries, both should
    // highlight when the user is on /trips* — startsWith handles that.
    return location === href || location.startsWith(href + "/");
  };

  // Task #2616: when the user is anywhere under /settings (or a sub-section
  // such as /settings?section=payments) we highlight the new "Account
  // settings" footer entry exactly like the other sidebar items.
  const settingsActive = location === "/settings" || location.startsWith("/settings/") || location.startsWith("/settings?");

  // Close the account dropdown on navigation so it never stays open behind
  // the next page after a link is clicked.
  useEffect(() => {
    setAccountMenuOpen(false);
    setMobileNavOpen(false);
  }, [location]);

  const initials = profile
    ? `${(profile.first_name || "")[0] || ""}${(profile.last_name || "")[0] || ""}`.toUpperCase() || "U"
    : "U";
  const displayName = profile
    ? [profile.first_name, profile.last_name].filter(Boolean).join(" ") || "Account"
    : "Account";

  const handleToggleMode = () => {
    toggle();
    // After flipping, drop the user on the canonical landing page for the
    // *new* mode so they don't sit on a now-mismatched URL waiting for the
    // ModeRouteSync guard to flip them back. `mode` here is the *current*
    // value; the new mode is the opposite.
    navigate(mode === "host" ? "/trips" : "/dashboard");
  };

  return (
    <div className={cn("dashboard-shell flex h-screen overflow-hidden max-md:h-dvh", presentation === "refined" && "dashboard-refined")}>
      {/*
        Dashboard sidebar (desktop-only, hidden on <md). Previously the rail
        was a 72px icon-only column with hover tooltips — feedback flagged
        that users couldn't tell which icon did what without hovering. We
        now show labels inline; the rail widened from 72px to 220px and
        the hover tooltip spans + per-row `title` attributes were removed
        (the visible text label is the accessible name). The right-edge
        active-indicator pill was also removed since the full-width pill
        background already communicates the active state when labels are
        visible. The home-logo link keeps its `aria-label` since the logo
        is still icon-only.
      */}
      <aside className="hidden w-[220px] shrink-0 flex-col bg-brand md:flex">
        <div className="flex items-center px-4 pt-5 pb-4">
          {/* Bug-report: replaced the placeholder square `ds-logo-square.png`
              with the canonical platform wordmark (light variant for
              the dark `bg-brand` sidebar) so the renter `/trips` and host
              dashboard show the real product logo. */}
          <Link href="/" className="block max-w-full" aria-label={t("dashboard.layout.go_to_homepage", "Go to homepage")} title={t("dashboard.layout.go_to_homepage", "Go to homepage")}>
            <Logo variant="light" size="sm" className="block [&_img]:!h-9 [&_img]:max-w-[180px] [&_img]:object-contain" />
          </Link>
        </div>

        {/* Task #2616: account/avatar menu lives under the logo so account
            settings are always reachable from the host dashboard (the main
            top header — which normally hosts the avatar menu — is hidden
            on /dashboard routes). Dropdown surfaces quick links to the
            settings sub-sections plus sign-out. */}
        <div className="px-3 pb-3">
          <DropdownMenu open={accountMenuOpen} onOpenChange={setAccountMenuOpen}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={t("dashboard.layout.open_account_menu", "Open account menu")}
                className="cursor-pointer flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm font-medium text-white/85 transition-colors hover:bg-white/8 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                data-testid="dashboard-account-menu-trigger"
              >
                <Avatar className="h-8 w-8 shrink-0">
                  {profile?.profile_image && (
                    <AvatarImage src={profile.profile_image} alt={displayName} />
                  )}
                  <AvatarFallback className="bg-white/15 text-[11px] font-semibold text-white">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <span className="min-w-0 flex-1 truncate">{displayName}</span>
                <ChevronDown className="h-4 w-4 shrink-0 text-white/70" strokeWidth={1.75} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              sideOffset={6}
              className="w-60 overflow-hidden rounded-xl border border-gray-200 p-0 shadow-lg"
            >
              <DropdownMenuGroup className="py-1">
                <DropdownMenuItem asChild className="cursor-pointer px-4 py-2.5">
                  <Link href="/settings?section=personal" className="flex items-center gap-3">
                    <User className="h-[18px] w-[18px] text-gray-500" />
                    <span className="text-sm">{t("dashboard.layout.personal_information", "Personal information")}</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="cursor-pointer px-4 py-2.5">
                  <Link href="/settings?section=security" className="flex items-center gap-3">
                    <Lock className="h-[18px] w-[18px] text-gray-500" />
                    <span className="text-sm">{t("dashboard.layout.login_security", "Login & security")}</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="cursor-pointer px-4 py-2.5">
                  <Link href="/settings?section=payments" className="flex items-center gap-3">
                    <Wallet className="h-[18px] w-[18px] text-gray-500" />
                    <span className="text-sm">{t("dashboard.layout.payment_settings", "Payment Settings")}</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="cursor-pointer px-4 py-2.5">
                  <Link href="/settings?section=languages" className="flex items-center gap-3">
                    <Globe className="h-[18px] w-[18px] text-gray-500" />
                    <span className="text-sm">{t("dashboard.layout.languages_currency", "Languages & currency")}</span>
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuSeparator className="my-0" />
              <DropdownMenuItem
                onClick={() => signOut()}
                className="cursor-pointer px-4 py-2.5"
                data-testid="dashboard-account-menu-logout"
              >
                <LogOut className="h-[18px] w-[18px] text-gray-500" />
                <span className="text-sm">{t("dashboard.layout.logout", "Logout")}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="mx-4 border-t border-white/15" />

        <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-3 pt-3">
          {navEntries.map((entry) => {
            const active = isActive(entry.href);
            return (
              <Link
                key={`${entry.labelKey}-${entry.href}`}
                href={entry.href}
                className={cn(
        "cursor-pointer flex h-10 w-full items-center gap-3 rounded-lg px-3 text-sm font-medium transition-colors",
                  active
                    ? "bg-white/15 text-white"
                    : "text-white/70 hover:bg-white/8 hover:text-white",
                )}
              >
                <entry.icon className="h-5 w-5 shrink-0" strokeWidth={1.5} />
                <span className="truncate">{t(entry.labelKey, { defaultValue: entry.fallbackLabel ?? entry.labelKey })}</span>
                {entry.href === "/messages" && <DashboardMessageBadge count={unreadMessages} />}
              </Link>
            );
          })}
        </nav>

        <div className="flex flex-col gap-1 px-3 pb-3">
          <div className="mb-2 w-full border-t border-white/15" />
          {/* Inbox feedback: surface the Help Centre directly in the
              dashboard sidebar so hosts/renters don't have to dig
              through the avatar menu to reach support. Routes to the
              same /help knowledge base the public Header opens. */}
          {/* Task #2616: persistent entry point to /settings from the host
              dashboard. Uses the same active-state treatment as the main
              nav items, with prefix matching so /settings?section=… and
              any future /settings/* sub-routes still highlight. */}
          <Link
            href="/settings"
            data-testid="dashboard-sidebar-account-settings"
            className={cn(
        "cursor-pointer flex h-10 w-full items-center gap-3 rounded-lg px-3 text-sm font-medium transition-colors",
              settingsActive
                ? "bg-white/15 text-white"
                : "text-white/70 hover:bg-white/8 hover:text-white",
            )}
          >
            <Settings className="h-5 w-5 shrink-0" strokeWidth={1.5} />
            <span className="truncate">{t("dashboard.layout.account_settings", "Account settings")}</span>
          </Link>
          <Link
            href="/help"
            className="cursor-pointer flex h-10 w-full items-center gap-3 rounded-lg px-3 text-sm font-medium text-white/70 transition-colors hover:bg-white/8 hover:text-white"
          >
            <HelpCircle className="h-5 w-5 shrink-0" strokeWidth={1.5} />
            <span className="truncate">{t("dashboard.layout.help_centre", "Help Centre")}</span>
          </Link>
          {/* Mode toggle: only meaningful for users with is_host=true.
              Non-hosts have nothing to switch into so we hide the button
              entirely (the spec keeps "List your car" as their host CTA). */}
          {canHost && (
            <button
              type="button"
              onClick={handleToggleMode}
              title={mode === "host" ? t("dashboard.layout.switch_to_guest", "Switch to guest") : t("dashboard.layout.switch_to_hosting", "Switch to hosting")}
              className="cursor-pointer flex h-10 w-full items-center gap-3 rounded-lg px-3 text-sm font-medium text-white/70 transition-colors hover:bg-white/8 hover:text-white"
            >
              <ArrowLeftRight className="h-5 w-5 shrink-0" strokeWidth={1.5} />
              <span className="truncate">{mode === "host" ? t("dashboard.layout.switch_to_guest", "Switch to guest") : t("dashboard.layout.switch_to_hosting", "Switch to hosting")}</span>
            </button>
          )}
          <button
            onClick={() => signOut()}
            className="flex h-10 w-full items-center gap-3 rounded-lg px-3 text-sm font-medium text-white/70 transition-colors hover:bg-white/8 hover:text-white"
          >
            <LogOut className="h-5 w-5 shrink-0" strokeWidth={1.5} />
            <span className="truncate">{t("dashboard.layout.logout", "Logout")}</span>
          </button>
        </div>
      </aside>

      <div data-testid="dashboard-scroll-region" className="dashboard-scroll-region flex-1 overflow-y-auto bg-muted/30 max-md:min-h-0 max-md:min-w-0">
        {/* Task #2616 — mobile dashboard top bar. The sidebar is hidden
            below md and the global Header is suppressed on /dashboard
            routes, so without this bar a host on a phone has no in-app
            access to account settings or the avatar menu. The desktop
            (md+) sidebar continues to own these affordances; this bar
            is md:hidden so it never duplicates them. */}
        <div data-testid="dashboard-mobile-header" className="sticky top-0 z-40 flex items-center justify-between gap-2 border-b border-white/10 bg-brand px-3 py-2 pt-[max(0.5rem,env(safe-area-inset-top))] md:hidden">
          <div className="flex min-w-0 flex-1 items-center gap-1">
            {/* Host-menu access. The desktop sidebar carries the full nav
                (Overview, Calendar, Bookings, Vehicles, Earnings, Messages,
                Reviews, Notifications, Host Profile) but it's hidden below md,
                leaving phones with no way to reach those screens. This
                hamburger opens the same nav in a left sheet, styled to match
                the branded dark-green sidebar. */}
            <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  aria-label={unreadMessages > 0
                    ? `${t("dashboard.layout.open_menu", "Open menu")}, ${t("nav.messages")}, ${t("nav.unread_indicator_aria", { count: unreadMessages })}`
                    : t("dashboard.layout.open_menu", "Open menu")}
                  data-testid="dashboard-mobile-nav-trigger"
                  className="relative touch-44 cursor-pointer flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-full text-white/85 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                >
                  <Menu className="h-5 w-5" strokeWidth={1.75} />
                  <span aria-hidden="true">
                    <DashboardMessageBadge count={unreadMessages} className="absolute right-0 top-0" />
                  </span>
                </button>
              </SheetTrigger>
              <SheetContent
                side="left"
                className="flex w-[280px] max-w-[100vw] max-h-dvh flex-col gap-0 overflow-y-auto border-0 bg-brand p-0 pb-[env(safe-area-inset-bottom)] text-white [&>button]:text-white/80 [&>button]:hover:text-white"
              >
                <SheetTitle className="sr-only">{t("dashboard.layout.host_menu", "Host menu")}</SheetTitle>
                <div className="flex items-center px-4 pt-5 pb-4">
                  <Link
                    href="/"
                    className="block max-w-full"
                    aria-label={t("dashboard.layout.go_to_homepage", "Go to homepage")}
                  >
                    <Logo
                      variant="light"
                      size="sm"
                      className="block [&_img]:!h-8 [&_img]:max-w-[160px] [&_img]:object-contain"
                    />
                  </Link>
                </div>
                <div className="mx-4 border-t border-white/15" />
                <nav className="flex shrink-0 flex-col gap-1 px-3 pt-3">
                  {navEntries.map((entry) => {
                    const active = isActive(entry.href);
                    return (
                      <Link
                        key={`m-${entry.labelKey}-${entry.href}`}
                        href={entry.href}
                        data-testid="dashboard-mobile-nav-item"
                        onClick={() => setMobileNavOpen(false)}
                        className={cn(
                          "cursor-pointer flex h-11 w-full items-center gap-3 rounded-lg px-3 text-sm font-medium transition-colors",
                          active
                            ? "bg-white/15 text-white"
                            : "text-white/70 hover:bg-white/8 hover:text-white",
                        )}
                      >
                        <entry.icon className="h-5 w-5 shrink-0" strokeWidth={1.5} />
                        <span className="truncate">{t(entry.labelKey, { defaultValue: entry.fallbackLabel ?? entry.labelKey })}</span>
                        {entry.href === "/messages" && <DashboardMessageBadge count={unreadMessages} />}
                      </Link>
                    );
                  })}
                </nav>
                <div className="flex flex-col gap-1 px-3 pb-5 pt-2">
                  <div className="mb-2 w-full border-t border-white/15" />
                  <Link
                    href="/settings"
                    className={cn(
                      "cursor-pointer flex h-11 w-full items-center gap-3 rounded-lg px-3 text-sm font-medium transition-colors",
                      settingsActive
                        ? "bg-white/15 text-white"
                        : "text-white/70 hover:bg-white/8 hover:text-white",
                    )}
                  >
                    <Settings className="h-5 w-5 shrink-0" strokeWidth={1.5} />
                    <span className="truncate">{t("dashboard.layout.account_settings", "Account settings")}</span>
                  </Link>
                  <Link
                    href="/help"
                    className="cursor-pointer flex h-11 w-full items-center gap-3 rounded-lg px-3 text-sm font-medium text-white/70 transition-colors hover:bg-white/8 hover:text-white"
                  >
                    <HelpCircle className="h-5 w-5 shrink-0" strokeWidth={1.5} />
                    <span className="truncate">{t("dashboard.layout.help_centre", "Help Centre")}</span>
                  </Link>
                  {canHost && (
                    <button
                      type="button"
                      onClick={() => {
                        setMobileNavOpen(false);
                        handleToggleMode();
                      }}
                      className="cursor-pointer flex h-11 w-full items-center gap-3 rounded-lg px-3 text-sm font-medium text-white/70 transition-colors hover:bg-white/8 hover:text-white"
                    >
                      <ArrowLeftRight className="h-5 w-5 shrink-0" strokeWidth={1.5} />
                      <span className="truncate">
                        {mode === "host" ? t("dashboard.layout.switch_to_guest", "Switch to guest") : t("dashboard.layout.switch_to_hosting", "Switch to hosting")}
                      </span>
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => signOut()}
                    className="cursor-pointer flex h-11 w-full items-center gap-3 rounded-lg px-3 text-sm font-medium text-white/70 transition-colors hover:bg-white/8 hover:text-white"
                  >
                    <LogOut className="h-5 w-5 shrink-0" strokeWidth={1.5} />
                    <span className="truncate">{t("dashboard.layout.logout", "Logout")}</span>
                  </button>
                </div>
              </SheetContent>
            </Sheet>
            <Link href="/" className="flex min-w-0 flex-1 items-center" aria-label={t("dashboard.layout.go_to_homepage", "Go to homepage")}>
              <Logo variant="light" size="sm" className="block min-w-0 max-w-full [&_img]:!h-[28px] [&_img]:min-w-0 [&_img]:max-w-[140px] [&_img]:object-contain" />
            </Link>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <Link
              href="/settings"
              data-testid="dashboard-mobile-account-settings"
              aria-label={t("dashboard.layout.account_settings", "Account settings")}
              className={cn(
        "touch-44 cursor-pointer flex h-[44px] w-[44px] items-center justify-center rounded-full transition-colors",
                settingsActive ? "bg-white/20 text-white" : "text-white/85 hover:bg-white/10 hover:text-white",
              )}
            >
              <Settings className="h-5 w-5" strokeWidth={1.75} />
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label={t("dashboard.layout.open_account_menu", "Open account menu")}
                  data-testid="dashboard-mobile-account-menu-trigger"
                  className="touch-44 cursor-pointer flex h-[44px] w-[44px] items-center justify-center rounded-full transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                >
                  <Avatar className="h-[32px] w-[32px]">
                    {profile?.profile_image && (
                      <AvatarImage src={profile.profile_image} alt={displayName} />
                    )}
                    <AvatarFallback className="bg-white/15 text-[11px] font-semibold text-white">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                sideOffset={6}
                className="w-60 overflow-hidden rounded-xl border border-gray-200 p-0 shadow-lg"
              >
                <DropdownMenuGroup className="py-1">
                  <DropdownMenuItem asChild className="cursor-pointer px-4 py-2.5">
                    <Link href="/settings?section=personal" className="flex items-center gap-3">
                      <User className="h-[18px] w-[18px] text-gray-500" />
                      <span className="text-sm">{t("dashboard.layout.personal_information", "Personal information")}</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="cursor-pointer px-4 py-2.5">
                    <Link href="/settings?section=security" className="flex items-center gap-3">
                      <Lock className="h-[18px] w-[18px] text-gray-500" />
                      <span className="text-sm">{t("dashboard.layout.login_security", "Login & security")}</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="cursor-pointer px-4 py-2.5">
                    <Link href="/settings?section=payments" className="flex items-center gap-3">
                      <Wallet className="h-[18px] w-[18px] text-gray-500" />
                      <span className="text-sm">{t("dashboard.layout.payment_settings", "Payment Settings")}</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="cursor-pointer px-4 py-2.5">
                    <Link href="/settings?section=languages" className="flex items-center gap-3">
                      <Globe className="h-[18px] w-[18px] text-gray-500" />
                      <span className="text-sm">{t("dashboard.layout.languages_currency", "Languages & currency")}</span>
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator className="my-0" />
                <DropdownMenuItem asChild className="cursor-pointer px-4 py-2.5">
                  <Link href="/help" className="flex items-center gap-3">
                    <HelpCircle className="h-[18px] w-[18px] text-gray-500" />
                    <span className="text-sm">{t("dashboard.layout.help_centre", "Help Centre")}</span>
                  </Link>
                </DropdownMenuItem>
                {canHost && (
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault();
                      handleToggleMode();
                    }}
                    className="cursor-pointer px-4 py-2.5"
                  >
                    <ArrowLeftRight className="h-[18px] w-[18px] text-gray-500" />
                    <span className="text-sm">
                      {mode === "host" ? t("dashboard.layout.switch_to_guest", "Switch to guest") : t("dashboard.layout.switch_to_hosting", "Switch to hosting")}
                    </span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="my-0" />
                <DropdownMenuItem
                  onClick={() => signOut()}
                  className="cursor-pointer px-4 py-2.5"
                  data-testid="dashboard-mobile-account-menu-logout"
                >
                  <LogOut className="h-[18px] w-[18px] text-gray-500" />
                  <span className="text-sm">{t("dashboard.layout.logout", "Logout")}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <VerificationHoldBanner />
        {children}
      </div>
    </div>
  );
}
